import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@monorepo/shared-auth';
import {
  IconCircleX,
  IconScale,
  IconBallBasketball,
  IconBallFootball,
  IconBallAmericanFootball,
  IconBallVolleyball,
  IconBallTennis,
  IconBallBaseball,
  IconBallBowling,
  IconGolf,
  IconTargetArrow,
  IconUsers,
  IconKarate,
  IconTrophy,
  IconDeviceGamepad2,
  IconDots,
  IconChevronDown,
  IconChevronUp
} from '@tabler/icons-react';
import { createLeague } from '../../services/leagueService';
import {
  SPORTS_LIST,
  LEAGUE_FORMATS,
  LEAGUE_VISIBILITY
} from '../../data/leagueSports';
import { filterCities } from '../../data/usCities';
import {
  LEAGUE_TERMS_TITLE,
  LEAGUE_TERMS_CHECKBOX_LABEL,
  LEAGUE_TERMS_SHORT_NOTICE,
  LEAGUE_TERMS_VERSION
} from './Legal';
import TermsModal from './Legal/TermsModal';

// Map sport icon names to actual icon components
const SPORT_ICONS = {
  'BallBasketball': IconBallBasketball,
  'BallFootball': IconBallFootball,
  'BallAmericanFootball': IconBallAmericanFootball,
  'BallVolleyball': IconBallVolleyball,
  'BallTennis': IconBallTennis,
  'BallBaseball': IconBallBaseball,
  'BallBowling': IconBallBowling,
  'Golf': IconGolf,
  'TargetArrow': IconTargetArrow,
  'Users': IconUsers,
  'Karate': IconKarate,
  'Trophy': IconTrophy,
  'DeviceGamepad2': IconDeviceGamepad2,
  'Dots': IconDots
};

const CreateLeagueForm = ({ onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    name: '',
    sport: '',
    customSportName: '', // For "Other" sport selection
    format: 'standard-league',
    visibility: LEAGUE_VISIBILITY.INVITE_ONLY,
    teamBased: false,
    maxParticipants: 16, // Under 120 = Free, 120+ = $1/member/month
    description: '',
    city: '',
    venue: '',
    rules: '',
    startDate: '',
    endDate: ''
  });

  const [showAllSports, setShowAllSports] = useState(false);

  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // City autocomplete state
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const cityInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSportChange = (sportId) => {
    const sport = SPORTS_LIST.find(s => s.id === sportId);
    setFormData(prev => ({
      ...prev,
      sport: sportId,
      teamBased: sport ? sport.teamBased : false
    }));
  };

  // Handle city input change with autocomplete
  const handleCityChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, city: value }));

    if (value.length >= 2) {
      const suggestions = filterCities(value);
      setCitySuggestions(suggestions);
      setShowCitySuggestions(suggestions.length > 0);
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };

  // Handle city selection from suggestions
  const handleCitySelect = (city) => {
    setFormData(prev => ({ ...prev, city }));
    setShowCitySuggestions(false);
    setCitySuggestions([]);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        cityInputRef.current &&
        !cityInputRef.current.contains(event.target)
      ) {
        setShowCitySuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load Square Web Payments SDK for paid tier (120+ members)
  useEffect(() => {
    if (currentStep === 2 && formData.maxParticipants >= 120) {
      const script = document.createElement('script');
      script.src = 'https://sandbox.web.squarecdn.com/v1/square.js';
      script.async = true;
      script.onload = initializeSquarePayment;
      document.body.appendChild(script);

      return () => {
        // Cleanup script on unmount
        document.body.removeChild(script);
      };
    }
  }, [currentStep, formData.maxParticipants]);

  const initializeSquarePayment = async () => {
    if (!window.Square) {
      throw new Error('Square.js failed to load properly');
    }

    try {
      const payments = window.Square.payments(
        'sandbox-sq0idb-HIBO4q1n3NF0GQWXfOv1tA',
        'LJVC4WWG2XVY9'
      );

      const card = await payments.card();
      await card.attach('#card-container');

      // Store card instance for payment processing
      window.squareCard = card;
    } catch (error) {
      console.error('Failed to initialize Square payment:', error);
      setPaymentError('Failed to load payment form. Please refresh and try again.');
    }
  };

  const processPayment = async () => {
    if (!window.squareCard) {
      setPaymentError('Payment form not initialized');
      return false;
    }

    setPaymentProcessing(true);
    setPaymentError('');

    try {
      const result = await window.squareCard.tokenize();

      if (result.status === 'OK') {
        // Send payment token to backend Cloud Function
        const response = await fetch('https://us-central1-southern-list-468215-m7.cloudfunctions.net/processPayment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sourceId: result.token,
            amount: calculateTotalCost() * 100, // Convert to cents
            currency: 'USD',
            leagueName: formData.name,
            participants: formData.maxParticipants,
          }),
        });

        const data = await response.json();

        if (data.success) {
          // Payment successful, proceed to next step
          return true;
        } else {
          setPaymentError(data.error || 'Payment failed');
          return false;
        }
      } else {
        let errorMessage = 'Payment failed';
        if (result.errors && result.errors.length > 0) {
          errorMessage = result.errors[0].message;
        }
        setPaymentError(errorMessage);
        return false;
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setPaymentError('An error occurred while processing payment');
      return false;
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await createLeague(currentUser.uid, formData);
      if (result.success) {
        if (onSuccess) {
          onSuccess(result.id);
        } else {
          navigate(`/leagues/${result.id}`);
        }
      }
    } catch (err) {
      setError('Failed to create league. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Determine if league requires payment based on member count
  const isPaidLeague = formData.maxParticipants >= 120;

  const nextStep = async () => {
    // Skip payment step if under 120 members (Free tier)
    if (currentStep === 1 && !isPaidLeague) {
      setCurrentStep(3); // Skip to step 3 (Basics)
    } else if (currentStep === 2 && isPaidLeague) {
      // Process payment before proceeding
      const paymentSuccess = await processPayment();
      if (paymentSuccess) {
        setCurrentStep(currentStep + 1);
      }
    } else if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    // Handle going back from step 3 when Free tier (should go to step 1)
    if (currentStep === 3 && !isPaidLeague) {
      setCurrentStep(1);
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const calculateTotalCost = () => {
    if (formData.maxParticipants >= 120) {
      return formData.maxParticipants * 1; // $1 per participant/month
    }
    return 0;
  };

  const renderStepIndicator = () => (
    <div className="ls-step-indicator">
      <div className={`ls-step ${currentStep >= 1 ? 'ls-active' : ''}`}>
        <span className="ls-step-number">1</span>
        <span className="ls-step-label">Pricing</span>
      </div>
      {isPaidLeague && (
        <div className={`ls-step ${currentStep >= 2 ? 'ls-active' : ''}`}>
          <span className="ls-step-number">2</span>
          <span className="ls-step-label">Payment</span>
        </div>
      )}
      <div className={`ls-step ${currentStep >= 3 ? 'ls-active' : ''}`}>
        <span className="ls-step-number">{isPaidLeague ? '3' : '2'}</span>
        <span className="ls-step-label">Basics</span>
      </div>
      <div className={`ls-step ${currentStep >= 4 ? 'ls-active' : ''}`}>
        <span className="ls-step-number">{isPaidLeague ? '4' : '3'}</span>
        <span className="ls-step-label">Format</span>
      </div>
      <div className={`ls-step ${currentStep >= 5 ? 'ls-active' : ''}`}>
        <span className="ls-step-number">{isPaidLeague ? '5' : '4'}</span>
        <span className="ls-step-label">Details</span>
      </div>
    </div>
  );

  const renderPricingStep = () => {
    const isPaidTier = formData.maxParticipants >= 120;
    const monthlyEstimate = isPaidTier ? formData.maxParticipants * 1 : 0;
    const yearlyEstimate = monthlyEstimate * 12;

    return (
      <div className="ls-form-step">
        <h3>Pricing</h3>
        <p className="ls-step-description">Our pricing is based on league size - smaller leagues are completely free!</p>

        <div className="ls-pricing-info-card">
          <div className="ls-pricing-tiers-display">
            <div className={`ls-tier-box ${!isPaidTier ? 'ls-tier-active' : ''}`}>
              <div className="ls-tier-badge ls-free-badge">Free Tier</div>
              <h4>Under 120 Members</h4>
              <div className="ls-pricing-amount">
                <span className="ls-price">$0</span>
                <span className="ls-period">/forever</span>
              </div>
              <p>All features included at no cost</p>
            </div>

            <div className={`ls-tier-box ${isPaidTier ? 'ls-tier-active' : ''}`}>
              <div className="ls-tier-badge ls-paid-badge">Paid Tier</div>
              <h4>120+ Members</h4>
              <div className="ls-pricing-amount">
                <span className="ls-price">$1</span>
                <span className="ls-period">/member/month</span>
              </div>
              <p>Billed monthly while league is active</p>
            </div>
          </div>

          <div className="ls-pricing-calculator">
            <h4>Set Your League Size</h4>
            <div className="ls-participant-input-row">
              <label htmlFor="maxParticipantsPricing">Expected Members:</label>
              <input
                type="number"
                id="maxParticipantsPricing"
                name="maxParticipants"
                value={formData.maxParticipants}
                onChange={handleInputChange}
                min="2"
                max="500"
              />
            </div>

            <div className="ls-cost-estimate">
              {isPaidTier ? (
                <>
                  <div className="ls-estimate-row">
                    <span>Monthly Cost:</span>
                    <span className="ls-estimate-value">${monthlyEstimate.toLocaleString()}/month</span>
                  </div>
                  <div className="ls-estimate-row ls-estimate-yearly">
                    <span>Year-Round League:</span>
                    <span className="ls-estimate-value">${yearlyEstimate.toLocaleString()}/year</span>
                  </div>
                  <p className="ls-estimate-note">
                    Example: A 200-member year-round league = $200/month = $2,400/year
                  </p>
                </>
              ) : (
                <div className="ls-free-confirmation">
                  <span className="ls-free-check">✓</span>
                  <span>Your league qualifies for the <strong>Free Tier</strong> - no payment required!</span>
                </div>
              )}
            </div>
          </div>

          <ul className="ls-pricing-features">
            <li>All league management features included</li>
            <li>Schedule generation & standings</li>
            <li>Member management & communication</li>
            <li>Archive leagues anytime to pause billing</li>
          </ul>
        </div>

        <div className="ls-info-note">
          <p><strong>Note:</strong> You can archive your league at any time to stop billing while keeping all your data. Archived leagues can be reactivated when you're ready to resume.</p>
        </div>

        <div className="ls-info-note">
          <p><strong>Enterprise Plan:</strong> For custom branding, white-label solutions, or multi-league organizations, please visit our <a href="/pricing" target="_blank">pricing page</a> to contact sales.</p>
        </div>
      </div>
    );
  };

  const renderPaymentStep = () => {
    const monthlyTotal = formData.maxParticipants * 1;

    return (
      <div className="ls-form-step">
        <h3>Payment Setup</h3>
        <p className="ls-step-description">Set up your monthly billing for your {formData.maxParticipants}-member league</p>

        <div className="ls-payment-summary">
          <h4>Billing Summary</h4>
          <div className="ls-summary-row">
            <span>Members:</span>
            <span>{formData.maxParticipants}</span>
          </div>
          <div className="ls-summary-row">
            <span>Rate:</span>
            <span>$1.00/member/month</span>
          </div>
          <div className="ls-summary-row ls-total">
            <span>Monthly Total:</span>
            <span>${monthlyTotal.toLocaleString()}/month</span>
          </div>
          <div className="ls-summary-row ls-yearly-preview">
            <span>If active year-round:</span>
            <span>${(monthlyTotal * 12).toLocaleString()}/year</span>
          </div>
        </div>

        <div id="payment-form-container">
          <div id="card-container"></div>
          {paymentError && <div className="ls-payment-error">{paymentError}</div>}
        </div>

        <div className="ls-info-note">
          <p><strong>First charge:</strong> ${monthlyTotal.toLocaleString()} will be charged today. Subsequent charges occur monthly while your league is active.</p>
        </div>

        <div className="ls-info-note">
          <p><strong>Archive anytime:</strong> You can archive your league at any point to pause billing. Your data will be preserved and you can reactivate when ready.</p>
        </div>
      </div>
    );
  };

  // Get sport icon component
  const getSportIcon = (iconName) => {
    const IconComponent = SPORT_ICONS[iconName];
    return IconComponent ? <IconComponent size={24} /> : <IconDots size={24} />;
  };

  // Popular sports shown by default
  const popularSports = SPORTS_LIST.filter(s => s.category === 'Popular');
  const otherSports = SPORTS_LIST.filter(s => s.category !== 'Popular' && s.id !== 'other');
  const otherOption = SPORTS_LIST.find(s => s.id === 'other');

  const renderBasicsStep = () => (
    <div className="ls-form-step">
      <h3>Competition Basics</h3>

      <div className="ls-form-group">
        <label htmlFor="name">League/Tournament Name</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
          placeholder="e.g., Summer Pickleball League 2025"
        />
      </div>

      <div className="ls-form-group">
        <label>Select Your Sport</label>
        <div className="ls-sport-grid">
          {popularSports.map(sport => (
            <button
              key={sport.id}
              type="button"
              className={`ls-sport-card ${formData.sport === sport.id ? 'ls-selected' : ''}`}
              onClick={() => handleSportChange(sport.id)}
            >
              <span className="ls-sport-icon">{getSportIcon(sport.icon)}</span>
              <span className="ls-sport-name">{sport.name}</span>
            </button>
          ))}
        </div>

        {showAllSports && (
          <div className="ls-sport-grid ls-sport-grid-more">
            {otherSports.map(sport => (
              <button
                key={sport.id}
                type="button"
                className={`ls-sport-card ls-sport-card-small ${formData.sport === sport.id ? 'ls-selected' : ''}`}
                onClick={() => handleSportChange(sport.id)}
              >
                <span className="ls-sport-icon">{getSportIcon(sport.icon)}</span>
                <span className="ls-sport-name">{sport.name}</span>
              </button>
            ))}
          </div>
        )}

        <div className="ls-sport-actions">
          <button
            type="button"
            className="ls-show-more-btn"
            onClick={() => setShowAllSports(!showAllSports)}
          >
            {showAllSports ? (
              <><IconChevronUp size={18} /> Show Less</>
            ) : (
              <><IconChevronDown size={18} /> Show More Sports</>
            )}
          </button>

          <button
            type="button"
            className={`ls-sport-card ls-sport-card-other ${formData.sport === 'other' ? 'ls-selected' : ''}`}
            onClick={() => handleSportChange('other')}
          >
            <span className="ls-sport-icon"><IconDots size={20} /></span>
            <span className="ls-sport-name">Other</span>
          </button>
        </div>

        {formData.sport === 'other' && (
          <div className="ls-custom-sport-input">
            <input
              type="text"
              name="customSportName"
              value={formData.customSportName}
              onChange={handleInputChange}
              placeholder="Enter your sport name..."
            />
          </div>
        )}
      </div>

      <div className="ls-form-group">
        <label>Competition Type</label>
        <div className="ls-type-toggle">
          <button
            type="button"
            className={`ls-type-btn ${!formData.teamBased ? 'ls-active' : ''}`}
            onClick={() => setFormData(prev => ({ ...prev, teamBased: false }))}
          >
            Individual
          </button>
          <button
            type="button"
            className={`ls-type-btn ${formData.teamBased ? 'ls-active' : ''}`}
            onClick={() => setFormData(prev => ({ ...prev, teamBased: true }))}
          >
            Team-Based
          </button>
        </div>
      </div>

      <div className="ls-optional-section">
        <p className="ls-optional-label">Optional Location Details</p>
        <div className="ls-form-row">
          <div className="ls-form-group ls-autocomplete-wrapper">
            <label htmlFor="city">City</label>
            <input
              ref={cityInputRef}
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleCityChange}
              placeholder="e.g., New York, NY"
              autoComplete="off"
            />
            {showCitySuggestions && citySuggestions.length > 0 && (
              <div ref={suggestionsRef} className="ls-autocomplete-suggestions">
                {citySuggestions.map((city, index) => (
                  <div
                    key={index}
                    className="ls-suggestion-item"
                    onClick={() => handleCitySelect(city)}
                  >
                    {city}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ls-form-group">
            <label htmlFor="venue">Venue</label>
            <input
              type="text"
              id="venue"
              name="venue"
              value={formData.venue}
              onChange={handleInputChange}
              placeholder="e.g., Central Park Courts"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderFormatStep = () => (
    <div className="ls-form-step">
      <h3>Competition Format</h3>
      <p className="ls-step-description">Choose how your league or tournament will be structured</p>

      <div className="ls-format-selection">
        {Object.values(LEAGUE_FORMATS).map(format => (
          <label key={format.id} className="ls-format-option">
            <input
              type="radio"
              name="format"
              value={format.id}
              checked={formData.format === format.id}
              onChange={handleInputChange}
            />
            <div className="ls-format-card">
              <div className="ls-format-header">
                <h4>{format.name}</h4>
                <span className="ls-format-type-badge">{format.type}</span>
              </div>
              <p>{format.description}</p>
              <span className="ls-format-meta">
                Min. {format.minParticipants} participants
              </span>
            </div>
          </label>
        ))}
      </div>

      <div className="ls-form-group">
        <label htmlFor="maxParticipants">Max Participants</label>
        <input
          type="number"
          id="maxParticipants"
          name="maxParticipants"
          value={formData.maxParticipants}
          onChange={handleInputChange}
          min="2"
          max="128"
        />
      </div>
    </div>
  );

  const renderDetailsStep = () => (
    <div className="ls-form-step">
      <h3>Additional Details</h3>

      <div className="ls-form-group">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows="4"
          placeholder="Tell participants about your league..."
        />
      </div>

      <div className="ls-form-row">
        <div className="ls-form-group">
          <label htmlFor="startDate">Start Date</label>
          <input
            type="date"
            id="startDate"
            name="startDate"
            value={formData.startDate}
            onChange={handleInputChange}
          />
        </div>

        <div className="ls-form-group">
          <label htmlFor="endDate">End Date</label>
          <input
            type="date"
            id="endDate"
            name="endDate"
            value={formData.endDate}
            onChange={handleInputChange}
          />
        </div>
      </div>

      <div className="ls-form-group">
        <label htmlFor="rules">Competition Rules</label>
        <textarea
          id="rules"
          name="rules"
          value={formData.rules}
          onChange={handleInputChange}
          rows="4"
          placeholder="Any specific rules or guidelines..."
        />
      </div>

      <div className="ls-terms-section">
        <div className="ls-terms-notice">
          <IconScale size={20} />
          <p>{LEAGUE_TERMS_SHORT_NOTICE}</p>
        </div>
        <label className="ls-terms-checkbox">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            required
          />
          <span>
            {LEAGUE_TERMS_CHECKBOX_LABEL}
            <button
              type="button"
              className="ls-terms-link"
              onClick={() => setShowTermsModal(true)}
            >
              (View Full Terms)
            </button>
          </span>
        </label>
      </div>
    </div>
  );

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="ls-create-league-modal" onClick={handleClose}>
      <div className="ls-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="ls-modal-header">
          <h2>Create League or Tournament</h2>
          <button onClick={handleClose} className="ls-close-btn" type="button">
            <IconCircleX size={28} />
          </button>
        </div>

        {renderStepIndicator()}

        <form onSubmit={handleSubmit} className="ls-create-league-form">
          {currentStep === 1 && renderPricingStep()}
          {currentStep === 2 && renderPaymentStep()}
          {currentStep === 3 && renderBasicsStep()}
          {currentStep === 4 && renderFormatStep()}
          {currentStep === 5 && renderDetailsStep()}

          {error && <div className="ls-error-message">{error}</div>}

          <div className="ls-form-actions">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="ls-btn-secondary"
                disabled={loading || paymentProcessing}
              >
                Previous
              </button>
            )}

            {currentStep < 5 ? (
              <button
                type="button"
                onClick={nextStep}
                className="ls-btn-primary"
                disabled={
                  (currentStep === 3 && (!formData.name || !formData.sport || (formData.sport === 'other' && !formData.customSportName))) ||
                  paymentProcessing
                }
              >
                {currentStep === 2 && isPaidLeague ?
                  (paymentProcessing ? 'Processing...' : 'Complete Payment') :
                  'Next'}
              </button>
            ) : (
              <button
                type="submit"
                className="ls-btn-primary"
                disabled={loading || !termsAccepted}
              >
                {loading ? 'Creating...' : 'Create Competition'}
              </button>
            )}
          </div>
        </form>

        {showTermsModal && (
          <TermsModal
            onClose={() => setShowTermsModal(false)}
            showAcceptButton={!termsAccepted}
            onAccept={() => {
              setTermsAccepted(true);
              setShowTermsModal(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CreateLeagueForm;
